import { Capacitor } from '@capacitor/core';
import { LicenseManager } from '../utils/licenseManager';

const AUTH_STORAGE_KEY = 'regiq_auth_session';
const USER_SETTINGS_KEY = 'regiq_user_settings';
const SESSION_EXPIRE_TIME = 30 * 24 * 60 * 60 * 1000; // 30 days

export interface AuthSession {
  email: string;
  licenseNumber: string;
  deviceId: string;
  platform: string;
  tier: string;
  validUntil: string;
  issuedAt: number;
  isValid: boolean;
  sessionId?: string; // Server-side session ID for kill session detection
  appVersion?: string; // App version for version enforcement
}

export interface UserSettings {
  name: string;
  company: string;
  phone: string;
  email: string;
  address: string;
  postcode: string;
  dvlaRnpsSid: string;
  licenseKey?: string;
  lastLogin: number;
  templates?: any[];
  preferences?: Record<string, any>;
}

export interface RegistrationData {
  name: string;
  company: string;
  phone: string;
  email: string;
  address: string;
  postcode: string;
  dvlaRnpsSid: string;
  licenseKey?: string;
}

export const AuthService = {
  /**
   * Send OTP to user email (used for both registration and login)
   */
  sendOTP: async (email: string, isRegistration: boolean = false): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await fetch('https://us-central1-plate-license-sys.cloudfunctions.net/sendOTP', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, isRegistration })
      });

      const data = await response.json();
      if (!response.ok) {
        return { success: false, message: data.error || 'Failed to send OTP' };
      }

      return { success: true, message: data.message || 'Verification code sent to your email' };
    } catch (e: any) {
      return { success: false, message: e.message || 'Failed to send verification code' };
    }
  },

  /**
   * Register new user - creates account and OTP verification
   * If no license key provided, automatically requests trial
   */
  registerUser: async (
    registrationData: RegistrationData,
    otp: string,
    deviceId: string,
    platform: string
  ): Promise<{ success: boolean; session?: AuthSession; userSettings?: UserSettings; message: string }> => {
    try {
      // If no license key provided, automatically request a trial
      const trialRequested = !registrationData.licenseKey || registrationData.licenseKey.trim() === '';
      
      const requestData = {
        ...registrationData,
        otp,
        deviceId,
        platform,
        trialRequested // Tell server to create trial license if no license key
      };
      
      console.log('🔍 REGISTRATION DEBUG:');
      console.log('URL:', 'https://us-central1-plate-license-sys.cloudfunctions.net/registerUser');
      console.log('Request Data:', JSON.stringify(requestData, null, 2));
      
      const response = await fetch('https://us-central1-plate-license-sys.cloudfunctions.net/registerUser', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      console.log('Response Status:', response.status);
      console.log('Response Headers:', response.headers);
      console.log('Response OK:', response.ok);
      
      // Get response as text first to see what we actually received
      const responseText = await response.text();
      console.log('Raw Response:', responseText);
      
      // Try to parse as JSON
      let registerData;
      try {
        registerData = JSON.parse(responseText);
        console.log('Parsed JSON:', registerData);
      } catch (parseError) {
        console.error('JSON Parse Error:', parseError);
        console.error('Response was not valid JSON:', responseText.substring(0, 500));
        return { success: false, message: 'Server returned invalid response: ' + responseText.substring(0, 100) };
      }

      if (!response.ok) {
        return { success: false, message: registerData.error || 'Registration failed' };
      }

      // Server may return licenseNumber or licenseId depending on path
      const licenseNumber = registerData.licenseNumber || registerData.licenseId || '';
      const { tier, validUntil, sessionId } = registerData;

      // Create session
      const session: AuthSession = {
        email: registrationData.email,
        licenseNumber,
        deviceId,
        platform,
        tier,
        validUntil,
        issuedAt: Date.now(),
        isValid: true,
        sessionId // Store server session ID
      };

      // Create user settings
      const userSettings: UserSettings = {
        name: registrationData.name,
        company: registrationData.company,
        phone: registrationData.phone,
        email: registrationData.email,
        address: registrationData.address,
        postcode: registrationData.postcode,
        dvlaRnpsSid: registrationData.dvlaRnpsSid,
        licenseKey: registrationData.licenseKey,
        lastLogin: Date.now()
      };

      // Save to persistent storage
      await AuthService.saveSession(session);
      await AuthService.saveUserSettings(userSettings);
      
      // Save license locally for offline use (especially important for trial)
      if (licenseNumber && validUntil) {
        const licenseInfo = {
          key: licenseNumber,
          hwId: deviceId,
          tier: tier || 'Trial',
          expiryDate: new Date(validUntil).getTime(),
          isTrial: tier === 'Trial'
        };
        LicenseManager.saveLicense(licenseInfo);
        console.log('License saved locally:', licenseInfo);
      }

      return { 
        success: true, 
        session,
        userSettings,
        message: tier === 'Trial' ? 'Trial activated! You have 7 days to try RegIQ.' : 'Registration successful!'
      };
    } catch (e: any) {
      console.error('❌ REGISTRATION ERROR:', e);
      console.error('Error type:', typeof e);
      console.error('Error message:', e.message);
      console.error('Full error:', e);
      return { success: false, message: e.message || 'Registration failed' };
    }
  },

  /**
   * Login existing user - checks if they have active license
   */
  loginUser: async (
    email: string,
    otp: string,
    licenseKeyIfExpired?: string,
    deviceId?: string,
    platform?: string,
    selectedLicenseId?: string,
    confirmTransfer?: boolean
  ): Promise<{
    success: boolean;
    session?: AuthSession;
    userSettings?: UserSettings;
    trialExpired?: boolean;
    licenseExpired?: boolean;
    noLicenses?: boolean;
    requiresSelection?: boolean;
    requiresTransferConfirmation?: boolean;
    trialPlatformMismatch?: boolean;
    warning?: string;
    lockDays?: number;
    availableLicenses?: Array<{ licenseId: string; productType: string; expiryDate: string; status: string; platform?: string }>;
    versionBlocked?: boolean;
    minimumVersion?: string;
    latestVersion?: string;
    releaseNotes?: string;
    message: string
  }> => {
    try {
      if (!deviceId || !platform) {
        throw new Error('Device information required');
      }

      // Get system info with app version
      let systemInfo: any = {
        deviceId,
        platform,
        timestamp: Date.now()
      };

      // If running in Electron, get enhanced system info including appVersion
      if ((window as any).require) {
        try {
          const { ipcRenderer } = (window as any).require('electron');
          const enhancedInfo = await ipcRenderer.invoke('get-system-info');
          systemInfo = { ...systemInfo, ...enhancedInfo };
          console.log('✅ Got enhanced system info from Electron:', enhancedInfo);
        } catch (e) {
          console.warn('Could not get enhanced system info from Electron:', e);
        }
      } else if (Capacitor.isNativePlatform()) {
        // On Capacitor/Android, get device info from plugin
        try {
          // Use magic comment to ignore missing module in Electron builds
          const { DeviceId } = await import(/* webpackIgnore: true */ '@capacitor-community/device-id');
          const deviceInfo = await DeviceId.getId();
          systemInfo = { ...systemInfo, ...deviceInfo };
          console.log('✅ Got device info from Capacitor plugin:', deviceInfo);
        } catch (e) {
          console.warn('Could not get device info from Capacitor plugin:', e);
        }
      }

      console.log('📤 Sending login request with systemInfo:', systemInfo);

      const response = await fetch('https://us-central1-plate-license-sys.cloudfunctions.net/loginUser', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          otp,
          systemInfo,
          licenseKey: licenseKeyIfExpired || undefined,
          selectedLicenseId: selectedLicenseId || undefined,
          confirmTransfer: confirmTransfer || undefined
        })
      });

      console.log('📥 Login response status:', response.status);

      const loginData = await response.json();
      console.log('📋 Login response data:', loginData);

      // Check if device transfer requires confirmation (HTTP 409)
      if (response.status === 409 || loginData.requiresConfirmation) {
        console.warn('⚠️  Device transfer requires confirmation:', loginData);
        return {
          success: false,
          requiresTransferConfirmation: true,
          warning: loginData.warning,
          lockDays: loginData.lockDays,
          message: loginData.message || 'Device transfer requires confirmation'
        };
      }

      // Check if version is blocked (HTTP 426 Upgrade Required)
      if (response.status === 426 || loginData.versionBlocked) {
        console.warn('❌ Version blocked:', loginData);
        return {
          success: false,
          versionBlocked: true,
          minimumVersion: loginData.minimumVersion,
          latestVersion: loginData.latestVersion,
          releaseNotes: loginData.releaseNotes,
          message: loginData.error || 'App version no longer supported. Please update to continue.'
        };
      }

      // Check if trial expired
      if (loginData.trialExpired) {
        console.warn('❌ Trial expired');
        return {
          success: false,
          trialExpired: true,
          message: 'Your trial has expired. Please enter a license key to continue.'
        };
      }

      // Check if trial platform mismatch
      if (loginData.trialPlatformMismatch) {
        console.warn('❌ Trial platform mismatch:', loginData.trialPlatform, '→', loginData.currentPlatform);
        
        // If they have other licenses, show license selection
        if (loginData.availableLicenses && loginData.availableLicenses.length > 0) {
          return {
            success: false,
            requiresSelection: true,
            availableLicenses: loginData.availableLicenses,
            message: loginData.error || 'Your trial is locked to another platform. Please select a paid license to use on this device.'
          };
        }
        
        // Otherwise, ask for license key
        return {
          success: false,
          noLicenses: true,
          trialPlatformMismatch: true,
          message: loginData.error || 'Your trial is locked to another platform. Please enter a license key to use on this device.'
        };
      }

      // Check if license has expired (not trial, actual license expiry)
      if (loginData.licenseExpired) {
        console.warn('❌ License expired');
        return {
          success: false,
          licenseExpired: true,
          message: loginData.error || 'Your license has expired. Please enter a new license key or renew.'
        };
      }

      // Check if user needs to select a license (multiple licenses)
      if (loginData.requiresSelection && loginData.availableLicenses) {
        console.log('ℹ️  Multiple licenses available, user needs to select');
        return {
          success: false,
          requiresSelection: true,
          availableLicenses: loginData.availableLicenses,
          message: 'Please select which license to use'
        };
      }

      // Check if user has no licenses and needs to enter a license key
      if (loginData.noLicenses || loginData.requiresLicenseKey) {
        console.warn('❌ No licenses available');
        return {
          success: false,
          noLicenses: true,
          message: loginData.error || 'No active licenses found. Please enter a license key.'
        };
      }

      // If error response still includes other available licenses, show selection modal
      // (e.g. server auto-selected wrong platform license but user has compatible ones too)
      if (!response.ok) {
        if (loginData.availableLicenses && loginData.availableLicenses.length > 0) {
          console.log('ℹ️  Error with available licenses - showing selection modal');
          return {
            success: false,
            requiresSelection: true,
            availableLicenses: loginData.availableLicenses,
            message: loginData.error || 'Please select a license for this device'
          };
        }
        console.error('❌ HTTP error:', response.status, loginData);
        return { success: false, message: loginData.error || 'Login failed' };
      }

      // Server may return licenseNumber or licenseId
      const licenseNumber = loginData.licenseNumber || loginData.licenseId || '';
      const { tier, validUntil } = loginData;
      console.log('✅ Login successful, got:', { licenseNumber, tier, validUntil });

      // Create session
      const session: AuthSession = {
        email,
        licenseNumber,
        deviceId,
        platform,
        tier,
        validUntil,
        issuedAt: Date.now(),
        isValid: true,
        sessionId: loginData.sessionId // Store server session ID
      };

      // Get and update user settings
      let userSettings = await AuthService.getUserSettings();
      if (userSettings) {
        userSettings.lastLogin = Date.now();
        if (licenseKeyIfExpired) {
          userSettings.licenseKey = licenseKeyIfExpired;
        }
      } else {
        userSettings = {
          name: email.split('@')[0],
          company: '',
          phone: '',
          email,
          address: '',
          postcode: '',
          dvlaRnpsSid: '',
          lastLogin: Date.now()
        };
      }

      // Save to persistent storage
      console.log('💾 Saving session and settings...');
      await AuthService.saveSession(session);
      await AuthService.saveUserSettings(userSettings);
      console.log('✅ Login successful!', session);

      return { 
        success: true,
        session,
        userSettings,
        message: 'Login successful!'
      };
    } catch (e: any) {
      console.error('❌ Login error:', e);
      return { success: false, message: e.message || 'Login failed' };
    }
  },

  /**
   * Logout user
   */
  logout: async (): Promise<void> => {
    await AuthService.clearSession();
  },

  /**
   * Check if user is already registered (has settings)
   */
  isUserRegistered: async (): Promise<boolean> => {
    const settings = await AuthService.getUserSettings();
    return settings !== null;
  },

  /**
   * Save authentication session to persistent storage
   */
  saveSession: async (session: AuthSession): Promise<void> => {
    try {
      const storageName = Capacitor.isNativePlatform() ? 'ionic' : 'localStorage';
      
      if (storageName === 'ionic') {
        // Android/iOS storage
        const { Preferences } = await import('@capacitor/preferences');
        await Preferences.set({
          key: AUTH_STORAGE_KEY,
          value: JSON.stringify(session)
        });
      } else {
        // Browser storage
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
      }
    } catch (e) {
      console.error('Failed to save auth session:', e);
      throw e;
    }
  },

  /**
   * Retrieve stored session from persistent storage
   */
  getSession: async (): Promise<AuthSession | null> => {
    try {
      const storageName = Capacitor.isNativePlatform() ? 'ionic' : 'localStorage';
      let data = null;

      if (storageName === 'ionic') {
        const { Preferences } = await import('@capacitor/preferences');
        const result = await Preferences.get({ key: AUTH_STORAGE_KEY });
        data = result?.value;
      } else {
        data = localStorage.getItem(AUTH_STORAGE_KEY);
      }

      if (!data) return null;

      const session = JSON.parse(data) as AuthSession;
      console.log('[AUTH] Session loaded from storage:', {
        email: session.email,
        hasSessionId: !!session.sessionId,
        sessionId: session.sessionId
      });

      // Check if session is still valid (hasn't exceeded 30 days)
      const sessionAge = Date.now() - session.issuedAt;
      if (sessionAge > SESSION_EXPIRE_TIME) {
        await AuthService.clearSession();
        return null;
      }

      // Check if license has expired
      if (new Date(session.validUntil) < new Date()) {
        await AuthService.clearSession();
        return null;
      }

      return session;
    } catch (e) {
      console.error('Failed to retrieve auth session:', e);
      return null;
    }
  },

  /**
   * Clear authentication session
   */
  clearSession: async (): Promise<void> => {
    try {
      const storageName = Capacitor.isNativePlatform() ? 'ionic' : 'localStorage';

      if (storageName === 'ionic') {
        const { Preferences } = await import('@capacitor/preferences');
        await Preferences.remove({ key: AUTH_STORAGE_KEY });
      } else {
        localStorage.removeItem(AUTH_STORAGE_KEY);
      }
    } catch (e) {
      console.error('Failed to clear auth session:', e);
    }
  },

  /**
   * Save user settings (company, postcode, etc.)
   */
  saveUserSettings: async (settings: UserSettings): Promise<void> => {
    try {
      const storageName = Capacitor.isNativePlatform() ? 'ionic' : 'localStorage';
      settings.lastLogin = Date.now();

      if (storageName === 'ionic') {
        const { Preferences } = await import('@capacitor/preferences');
        await Preferences.set({
          key: USER_SETTINGS_KEY,
          value: JSON.stringify(settings)
        });
      } else {
        localStorage.setItem(USER_SETTINGS_KEY, JSON.stringify(settings));
      }
    } catch (e) {
      console.error('Failed to save user settings:', e);
      throw e;
    }
  },

  /**
   * Retrieve user settings
   */
  getUserSettings: async (): Promise<UserSettings | null> => {
    try {
      const storageName = Capacitor.isNativePlatform() ? 'ionic' : 'localStorage';
      let data = null;

      if (storageName === 'ionic') {
        const { Preferences } = await import('@capacitor/preferences');
        const result = await Preferences.get({ key: USER_SETTINGS_KEY });
        data = result?.value;
      } else {
        data = localStorage.getItem(USER_SETTINGS_KEY);
      }

      if (!data) return null;
      return JSON.parse(data) as UserSettings;
    } catch (e) {
      console.error('Failed to retrieve user settings:', e);
      return null;
    }
  },

  /**
   * Check if device switch is allowed (7-day lock)
   */
  checkDeviceLock: async (
    email: string,
    newDeviceId: string,
    newPlatform: string,
    licenseNumber: string
  ): Promise<{ allowed: boolean; message?: string; daysRemaining?: number }> => {
    try {
      const response = await fetch('https://us-central1-plate-license-sys.cloudfunctions.net/checkDeviceLock', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          newDeviceId,
          newPlatform,
          licenseNumber
        })
      });

      const data = await response.json();
      return {
        allowed: data.allowed !== false,
        message: data.message,
        daysRemaining: data.daysRemaining
      };
    } catch (e: any) {
      // If offline or error, allow (graceful fallback)
      console.warn('Device lock check failed, allowing access:', e.message);
      return { allowed: true };
    }
  },

  /**
   * Validate a license key against Firestore
   */
  validateLicenseKey: async (licenseKey: string): Promise<{ valid: boolean; tier?: string; validUntil?: string; message: string }> => {
    try {
      const response = await fetch('https://us-central1-plate-license-sys.cloudfunctions.net/validateLicenseKey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseKey })
      });

      const data = await response.json();
      if (!data.valid) {
        return { valid: false, message: data.message || 'Invalid license key' };
      }

      return {
        valid: true,
        tier: data.tier,
        validUntil: data.validUntil,
        message: data.message || 'License key is valid'
      };
    } catch (e: any) {
      return { valid: false, message: e.message || 'Failed to validate license key' };
    }
  }
};
